/**
 * Easing Functions for Poker Reborn Effects Engine
 *
 * Standard easing functions for smooth animations.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 4
 */

// =============================================================================
// LINEAR
// =============================================================================

/**
 * Linear easing (no easing).
 */
export function linear(t: number): number {
  return t;
}

// =============================================================================
// EASE IN (Acceleration)
// =============================================================================

/**
 * Quadratic ease-in (slow start).
 */
export function easeInQuad(t: number): number {
  return t * t;
}

/**
 * Cubic ease-in (slower start).
 */
export function easeInCubic(t: number): number {
  return t * t * t;
}

/**
 * Quartic ease-in.
 */
export function easeInQuart(t: number): number {
  return t * t * t * t;
}

/**
 * Quintic ease-in.
 */
export function easeInQuint(t: number): number {
  return t * t * t * t * t;
}

// =============================================================================
// EASE OUT (Deceleration)
// =============================================================================

/**
 * Quadratic ease-out (slow end).
 */
export function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/**
 * Cubic ease-out (slower end).
 * Great for cards arriving at destination.
 */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Quartic ease-out.
 */
export function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

/**
 * Quintic ease-out.
 */
export function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

// =============================================================================
// EASE IN-OUT (Acceleration + Deceleration)
// =============================================================================

/**
 * Quadratic ease-in-out.
 */
export function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Cubic ease-in-out.
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Quartic ease-in-out.
 */
export function easeInOutQuart(t: number): number {
  return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

// =============================================================================
// SPECIAL EFFECTS
// =============================================================================

/**
 * Elastic ease-out (bouncy overshoot).
 * Great for dramatic reveals.
 */
export function easeOutElastic(t: number): number {
  const c4 = (2 * Math.PI) / 3;

  return t === 0
    ? 0
    : t === 1
      ? 1
      : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
}

/**
 * Bounce ease-out.
 * Great for chips landing.
 */
export function easeOutBounce(t: number): number {
  const n1 = 7.5625;
  const d1 = 2.75;

  if (t < 1 / d1) {
    return n1 * t * t;
  } else if (t < 2 / d1) {
    return n1 * (t -= 1.5 / d1) * t + 0.75;
  } else if (t < 2.5 / d1) {
    return n1 * (t -= 2.25 / d1) * t + 0.9375;
  } else {
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  }
}

/**
 * Back ease-out (slight overshoot then settle).
 * Great for cards snapping into place.
 */
export function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;

  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/**
 * Back ease-in-out.
 */
export function easeInOutBack(t: number): number {
  const c1 = 1.70158;
  const c2 = c1 * 1.525;

  return t < 0.5
    ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
    : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
}

// =============================================================================
// STEP FUNCTIONS
// =============================================================================

/**
 * Step function - jumps instantly at midpoint.
 */
export function step(t: number): number {
  return t < 0.5 ? 0 : 1;
}

/**
 * Step function - jumps at start.
 */
export function stepStart(t: number): number {
  return t > 0 ? 1 : 0;
}

/**
 * Step function - jumps at end.
 */
export function stepEnd(t: number): number {
  return t >= 1 ? 1 : 0;
}

// =============================================================================
// EASING TYPE
// =============================================================================

/**
 * Easing function type.
 */
export type EasingFunction = (t: number) => number;

/**
 * Named easing functions lookup.
 */
export const easings: Record<string, EasingFunction> = {
  linear,
  easeInQuad,
  easeInCubic,
  easeInQuart,
  easeInQuint,
  easeOutQuad,
  easeOutCubic,
  easeOutQuart,
  easeOutQuint,
  easeInOutQuad,
  easeInOutCubic,
  easeInOutQuart,
  easeOutElastic,
  easeOutBounce,
  easeOutBack,
  easeInOutBack,
  step,
  stepStart,
  stepEnd,
};

/**
 * Get an easing function by name.
 */
export function getEasing(name: string): EasingFunction {
  return easings[name] ?? linear;
}
